import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRightFromBracket, faArrowsRotate, faBuilding, faShieldCheck } from "@fortawesome/pro-regular-svg-icons"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { CompaniesUsersItem } from "../../../../../users/userTypes"

type UserNameCellProps = Readonly<{
    user: Pick<CompaniesUsersItem, "displayName" | "lastLoginEvent" | "department" | "employeeId" | "roles" | "company">
}>

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: ".25rem",
    },
    nameRow: {
        display: "flex",
        alignItems: "center",
        gap: ".625rem",
        flexWrap: "wrap",
    },
    name: {
        fontWeight: 600,
        fontSize: ".875rem",
        color: theme.palette.tailwind.slate[900],
    },
    metaRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: ".875rem",
    },
    item: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".375rem",
        fontSize: ".8125rem",
        lineHeight: 1.2,
    },
    icon: {
        fontSize: ".8125rem",
    },
    faint: {
        color: theme.palette.tailwind.slate[400],
    },
    muted: {
        color: theme.palette.tailwind.slate[500],
    },
    strong: {
        color: theme.palette.tailwind.slate[700],
    },
    rights: {
        color: theme.palette.tailwind.blue[600],
    },
}))

export function UserNameCell({ user }: UserNameCellProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const lastLogin = user.lastLoginEvent?.timestamp
    const lastLoginRelative = lastLogin ? dateTimeFactory.fromApi(lastLogin).toRelative() : undefined
    const departmentName = user.department?.name
    const roleCount = user.roles?.length ?? 0
    const isSynced = user.company?.companySetup?.userSynchronization ?? false

    return (
        <Box className={classes.root}>
            <Box className={classes.nameRow}>
                <Box component="span" className={classes.name}>
                    {user.displayName}
                </Box>
                {lastLoginRelative && (
                    <Box className={cx(classes.item, classes.faint)}>
                        <FontAwesomeIcon icon={faArrowRightFromBracket} className={classes.icon} />
                        <span>{lastLoginRelative}</span>
                    </Box>
                )}
            </Box>

            <Box className={classes.metaRow}>
                {departmentName && (
                    <Box className={cx(classes.item, classes.muted)}>
                        <FontAwesomeIcon icon={faBuilding} className={classes.icon} />
                        <span>{departmentName}</span>
                    </Box>
                )}
                {user.employeeId && (
                    <Box className={cx(classes.item, classes.strong)}>
                        <LocalizedStrict id="company-users-cell-internal-id" vars={{ id: user.employeeId }}>
                            <span>Internal ID: {user.employeeId}</span>
                        </LocalizedStrict>
                    </Box>
                )}
                {roleCount > 0 && (
                    <Box className={cx(classes.item, classes.rights)}>
                        <FontAwesomeIcon icon={faShieldCheck} className={classes.icon} />
                        <LocalizedStrict id="company-users-cell-rights" vars={{ count: roleCount }}>
                            <span>{roleCount} rights</span>
                        </LocalizedStrict>
                    </Box>
                )}
                {isSynced && (
                    <Box className={cx(classes.item, classes.faint)}>
                        <FontAwesomeIcon icon={faArrowsRotate} className={classes.icon} />
                        <LocalizedStrict id="company-users-cell-sync-badge">
                            <span>Sync</span>
                        </LocalizedStrict>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
